import { Button } from '../components/ui/button';
import {
  LegalNav,
  LegalPageBody,
  LegalPageHero,
  LegalPanel,
  LegalSection,
  ReviewNotice,
  lastUpdated,
} from '../components/common/LegalPageShell';
import { openCookiePreferences } from '../lib/consent';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const sections = [
  { id: 'what-cookies-are', label: 'What cookies are' },
  { id: 'cookies-identified', label: 'Cookies identified' },
  { id: 'browser-storage', label: 'Browser storage' },
  { id: 'manage-preferences', label: 'Manage preferences' },
];

const CookiePolicyPage = () => {
  return (
    <div data-testid="cookie-policy-page">
      <Seo {...routeMeta['/cookie-policy']} path="/cookie-policy" />
      <LegalPageHero
        title="Cookie Policy"
        currentPage="Cookie Policy"
        description="What the reviewed site source stores in the browser, what is not currently present, and how visitors can control analytics consent."
        meta={[
          ['Updated', lastUpdated],
          ['Analytics', 'Consent only'],
        ]}
      />

      <LegalPageBody aside={<LegalNav sections={sections} />}>
        <ReviewNotice />
        <LegalPanel>
          <LegalSection id="what-cookies-are" title="What Cookies Are">
            <p>
              Cookies are small files stored by a website or third-party service on a visitor's device. They can help a site function, remember choices, or understand site usage.
            </p>
          </LegalSection>

          <LegalSection id="cookies-identified" title="Cookies Identified">
            <p>
              No first-party cookie-setting code was identified in the repository during discovery. The table below is intentionally not populated with guessed Google Analytics cookie names because Google Analytics is not currently loaded in the reviewed source.
            </p>

            <div className="mt-2 overflow-hidden border border-white/10">
              <table className="hidden w-full border-collapse text-left text-sm md:table">
                <caption className="sr-only">Cookies identified from the LEION Engineering site source</caption>
                <thead className="bg-white/10 text-xs uppercase tracking-[0.18em] text-[#BFDBFE]">
                  <tr>
                    <th scope="col" className="px-4 py-4">Cookie name</th>
                    <th scope="col" className="px-4 py-4">Set by</th>
                    <th scope="col" className="px-4 py-4">Purpose</th>
                    <th scope="col" className="px-4 py-4">Duration</th>
                    <th scope="col" className="px-4 py-4">Category</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-white/10">
                    <td colSpan="5" className="px-4 py-6 text-slate-300">
                      No site-set cookies confirmed from the reviewed code.
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="grid gap-3 p-4 md:hidden">
                <div className="border border-white/10 bg-white/[0.04] p-4">
                  <h3 className="font-['JetBrains_Mono'] text-xs font-bold uppercase tracking-[0.18em] text-[#BFDBFE]">
                    Cookie name
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">No site-set cookies confirmed from the reviewed code.</p>
                </div>
              </div>
            </div>
          </LegalSection>

          <LegalSection id="browser-storage" title="Browser Storage">
            <p>
              The reviewed source uses browser localStorage and sessionStorage for consent choice, first-party analytics identifiers, and the private analytics dashboard token. These are not cookies, but they are still browser storage.
            </p>
            <p>
              The analytics keys identified are <span className="font-['JetBrains_Mono'] text-sm text-[#BFDBFE]">leion_visitor_id</span>, <span className="font-['JetBrains_Mono'] text-sm text-[#BFDBFE]">leion_session_id</span>, and <span className="font-['JetBrains_Mono'] text-sm text-[#BFDBFE]">leion_session_started_at</span>. Analytics storage is only used after a visitor accepts analytics in the banner.
            </p>
          </LegalSection>

          <LegalSection id="manage-preferences" title="Manage Preferences">
            <p>
              Use Cookie preferences to accept analytics or reject analytics. Rejecting analytics clears the first-party analytics storage keys used by this site.
            </p>
            <div>
              <Button
                type="button"
                onClick={openCookiePreferences}
                className="min-h-12 rounded-sm border border-[#93C5FD] bg-transparent px-6 font-bold uppercase tracking-wider text-[#DBEAFE] hover:bg-[#93C5FD] hover:text-[#06101f]"
              >
                Open cookie preferences
              </Button>
            </div>
          </LegalSection>
        </LegalPanel>
      </LegalPageBody>
    </div>
  );
};

export default CookiePolicyPage;
